/* Chamadas à API da plataforma ZNIT (/api/v1/znit) ou ao backend simulado do modo demonstração. */

import { CFG, isApi } from './config';
import { sleep } from './formato';
import { demoApi } from '../demo/api';

export type Params = Record<string, string | number | undefined | null>;

export interface OpcoesApi {
  params?: Params;
  body?: unknown;
  form?: FormData;
  /** Vai ao backend mesmo no modo demonstração e sem autenticação (login). */
  publica?: boolean;
}

/** Disparado quando o backend recusa o token de sessão (expirou ou saiu em outro lugar). */
export const EVENTO_SESSAO_EXPIRADA = 'znit:sessao-expirada';

/** Erro devolvido pela API, com o status HTTP e o corpo da resposta. */
export class ErroApi extends Error {
  constructor(mensagem: string, readonly status?: number, readonly payload?: unknown) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

type Json = Record<string, unknown>;

/** O backend embrulha em {success,data,error,meta} e as rotas têm {status,data} por dentro. */
export function unwrap<T>(j: unknown): T {
  let x = j;
  for (let i = 0; i < 3; i++) {
    if (x && typeof x === 'object' && !Array.isArray(x) && 'data' in x && ('success' in x || 'status' in x)) x = (x as Json).data;
    else break;
  }
  return x as T;
}

export function msgErro(j: unknown): string {
  if (!j || typeof j !== 'object') return '';
  const o = j as Json;
  if (typeof o.error === 'string') return o.error;
  if (o.error && typeof o.error === 'object') return String((o.error as Json).message || '');
  return String(o.message || '');
}

/** Detalhes de um erro de importação (linha_excel, linhas_inseridas...). */
export function detalhesErro(e: unknown): Json {
  const p = ((e as ErroApi)?.payload || {}) as Json;
  const erro = p.error as Json | undefined;
  return ((erro && typeof erro === 'object' && (erro.details as Json)) || p) as Json;
}

export async function api<T = unknown>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, opcoes: OpcoesApi = {}): Promise<T> {
  const { params = {}, body, form, publica } = opcoes;
  if (!isApi() && !publica) {
    await sleep(160);
    return demoApi(method, path, params, body, form) as T;
  }
  if (!publica && !CFG.token && !CFG.apiKey) throw new ErroApi('Entre com o seu e-mail e senha.', 401);

  const url = new URL(`${CFG.base.replace(/\/+$/, '')}/api/v1/znit${path}`);
  // A empresa vem do seletor no topo; sem ela, o backend usa o primeiro CNPJ do usuário.
  if (CFG.cnpj) url.searchParams.set('cnpj', CFG.cnpj);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  });
  const headers: Record<string, string> = {};
  if (publica) { /* login: sem credencial */ }
  else if (CFG.token) headers.Authorization = `Bearer ${CFG.token}`;
  else headers['X-API-Key'] = CFG.apiKey;
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (form) init.body = form;

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new ErroApi(`Não foi possível conectar em ${CFG.base}. Confira a URL, se o backend está no ar e se o CORS libera esta origem.`);
  }
  let j: unknown = null;
  try {
    j = await res.json();
  } catch {
    /* resposta sem JSON */
  }
  const o = (j || {}) as Json;
  if (!res.ok || o.success === false || o.status === 'error') {
    if (res.status === 401 && CFG.token && !publica) window.dispatchEvent(new CustomEvent(EVENTO_SESSAO_EXPIRADA));
    throw new ErroApi(msgErro(j) || `Erro HTTP ${res.status}`, res.status, j);
  }
  return unwrap<T>(j);
}
